




import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { DxDataGrid } from 'devextreme-vue/data-grid';
import deepObjectMerge from 'lodash/merge';

import {
    CONTRACTS,
    EventsServiceContract,
    Events,
} from '@/core/contracts';
import { localize } from '@/core/filters';
import { upperFirst } from '@/core/helpers';
import { ContextsState, ProductsState } from '@/store';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { DataSource } from '@/types';
import {
    Table,
    TableConfig,
    TableColumn,
    TableComponentProps,
} from './types';
import defaultTableConfig from './defaultTableConfig';

@Component({
    components: {
        DxDataGrid,
    },
})
export default class TableComponent extends Vue implements TableComponentProps {
    @Prop({ type: String, required: true }) public tableKey!: string;
    // table data
    @Prop({ type: Array, required: true }) public columns!: TableColumn[];
    @Prop({ type: [Object, Array], required: true }) public dataSource!: DataSource;
    // TableConfig object, passed down from parent component
    // Will be merged with local computedTableConfig and deep merged with it
    @Prop({ type: Object, required: false }) public tableConfig?: Partial<TableConfig>;
    @Prop({ type: Boolean, default: true }) public shouldHandelWSEvents!: boolean;

    @Inject(CONTRACTS.EventsService) private _eventsService?: EventsServiceContract;

    public async mounted() {
        // raise instance of the table up to parent
        this.$emit('setTableInstance', this.tableInstance);

        if (this.shouldHandelWSEvents) {
            // add all declared WebSocket listeners
            Object.keys(this.webSocketEventsListeners).forEach((eventName) => {
                this._eventsService!.on(eventName, this.webSocketEventsListeners[eventName]);
            });
        }
    }

    public async beforeDestroy() {
        this.clearAllFilters();

        if (this.shouldHandelWSEvents) {
            // remove all declared WebSocket listeners
            Object.keys(this.webSocketEventsListeners).forEach((eventName) => {
                this._eventsService!.off(eventName, this.webSocketEventsListeners[eventName]);
            });
        }
    }

    // https://js.devexpress.com/Documentation/ApiReference/UI_Widgets/dxDataGrid/Configuration/
    public get computedTableConfig(): TableConfig {
        return Object.assign(
            {
                dataSource: this.dataSource,
                columns: this.transformColumns(this.columns),
            },
            defaultTableConfig(this),
            this.tableConfig,
        );
    }

    public get tableInstance(): Table {
        const element = this.$refs['data-grid'] as unknown;
        return (element as DxDataGrid).instance!;
    }

    // flag that the user interacts with the table (made any changes that should not be lost)
    private get isEditingNow(): boolean {
        const { tableInstance } = this;
        const haveModifiedItems = tableInstance.getSelectedRowsData().length > 0;

        return haveModifiedItems;
    }

    // all listeners for webSocket events to which the table will respond
    private get webSocketEventsListeners(): { [key: string]: (args: any[]) => void } {
        return {
            [Events.ProcessingFinished]: this.onProcessingFinishedEvent,
        };
    }

    private resized() {
        setTimeout(this.updateScrollbars, 500);
    }

    private updateScrollbars() {
        this.tableInstance.repaintRows([0]);
    }

    private transformColumns(columns: Array<TableColumn>): Array<TableColumn> {
        return columns.map((col) => {
            const { caption } = col;

            if (caption) {
                col.caption = localize(caption);
            }

            return col;
        });
    }

    private onProcessingFinishedEvent({ contextId }: any): void {
        // need to avoid loosing user input during the update, triggered by event from server
        if (Number(ContextsState.currentContextId) === Number(contextId) && !this.isEditingNow) {
            this.refreshTable();
        }
    }

    private refreshTable(): void {
        this.tableInstance.refresh();
    }

    private clearAllFilters(): void {
        const { tableInstance } = this;
        tableInstance.clearFilter();
    }
}
